import React from 'react';
import Dialog from 'components/pure/utils/Dialog';
import Button from '@material-ui/core/Button';
import connectToStores from 'alt-utils/lib/connectToStores';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import SkillActions from 'actions/SkillActions';
import AuthStore from 'stores/AuthStore';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import SkillStore from 'stores/SkillStore';
import AddToReview from 'components/pure/sharing/coaching/AddToReview';
import {List} from 'immutable';
import {CoachPlaceholder} from 'components/pure/sharing/coaching/placeholders/CoachPlaceholder';
import RepPlaceholder from 'components/pure/sharing/coaching/placeholders/RepPlaceholder';
import classNames from 'classnames';
import Loading from 'components/pure/utils/Loading';
import './AddToReviewDialog.styl';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import FileUploadStore from 'stores/files/FileUploadStore';

/**
 * Standalone add call to review dialog.
 */
@connectToStores
export default class AddToReviewDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      onSubmit: undefined,
      isDisabled: undefined,
      ready: false,
    };

    this.renderInnerDialog = this.renderInnerDialog.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      const {authUser} = this.props;

      // Avoiding dispatch during dispatch
      setImmediate(() => {
        this.setState({ready: false}, () => {
          // find any open plans where we are the coach or recipient.
          Promise.all([
            CoachingPlanActions.fetchAll({
              coachId: authUser.id,
              recipientId: authUser.id,
              includeCompleted: false,
              includeReviews: true,
            }),
            SkillActions.fetchAllByTeam({
              team_id: this.props.callerTeamId,
            }),
          ]).then(() => this.setState({ready: true}));
        });
      });
    }
  }

  static getStores() {
    return [AuthStore, SkillStore, CoachingPlanStore, CoachingReviewStore, FileUploadStore];
  }

  static getPropsFromStores(props) {
    const {user: authUser} = AuthStore.getState();
    const {coachingPlans, areCoachingPlansLoading} = CoachingPlanStore.getState();
    const {skillsByTeam, areSkillsFetchingByTeam} = SkillStore.getState();
    const {reviewRedirect} = CoachingReviewStore.getState();

    return {
      authUser,
      coachingPlans,
      areCoachingPlansLoading,
      skills: props.fileUpload ? skillsByTeam.get(props.fileUpload.caller.teamId) : List(),
      areSkillsFetchingByTeam,
      reviewRedirect,
    };
  }

  renderInnerDialog(isLoading, hasNoApplicablePlans, onLoad, applicableCoachingPlans) {
    const {authUser, onClose, onCoachNow, onCreatePlan, fileUpload} = this.props;

    if (isLoading)
      return (
        <div className="loading">
          <Loading />
        </div>
      );

    if (hasNoApplicablePlans) {
      return authUser.isManager() ? (
        <CoachPlaceholder
          isOwnCall={authUser.id === fileUpload.callDetails.callerId}
          onClose={onClose}
          onCoachNow={onCoachNow}
          onCreatePlan={onCreatePlan}
          callerName={fileUpload.callDetails.caller.getFullName()}
        />
      ) : (
        <RepPlaceholder onClose={onClose} />
      );
    }

    return (
      <AddToReview
        {...this.props}
        applicableCoachingPlans={applicableCoachingPlans}
        onLoad={onLoad}
        standalone={true}
      />
    );
  }

  onSubmit() {
    this.state.onSubmit().then(() => this.props.onClose());
  }

  render() {
    const {onClose, fileUpload, coachingPlans, open} = this.props;
    const {ready} = this.state;

    if (!fileUpload) return null;

    // Further refine the list to only plans where the caller
    // is also the recipient of the plan.
    const applicableCoachingPlans =
      coachingPlans && coachingPlans.size > 0
        ? coachingPlans.filter(p => p.recipient.id === fileUpload.caller.id)
        : List();

    const isLoading = !ready;
    const hasNoApplicablePlans = !isLoading && applicableCoachingPlans.size === 0;
    const isDisabled = this.state.isDisabled && this.state.isDisabled();
    const onLoad = (onSubmit, isDisabled) =>
      this.setState({
        onSubmit,
        isDisabled,
      });

    return (
      <Dialog
        className={classNames([
          'add-call-to-review-dialog',
          hasNoApplicablePlans && 'no-plans-placeholder-dialog',
        ])}
        modal={true}
        open={open}
        showTitleBar={true}
        title={'Add Call To Plan'}
        autoScrollBodyContent={false}
        onRequestClose={onClose}
        actions={
          !hasNoApplicablePlans && (
            <>
              <Button variant="outlined" className="ev-button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                className="ev-button"
                onClick={this.onSubmit}
                disabled={isDisabled}
                primary={true}>
                Add
              </Button>
            </>
          )
        }>
        {this.renderInnerDialog(isLoading, hasNoApplicablePlans, onLoad, applicableCoachingPlans)}
      </Dialog>
    );
  }
}
